/**
 * PDF Export functionality for RumahKira estimation reports
 */

import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { formatCurrency, formatNumber, formatDate } from './estimationEngine';
import { ASSUMPTIONS, DISCLAIMER } from './costRates';

/**
 * Generate a professional PDF summary report
 * @param {Object} results - Estimation results from calculateEstimation
 * @param {string} language - 'en' or 'my'
 */
export const generateEstimationPDF = (results, language = 'en') => {
  const { costs, breakdown, materials, inputs, generatedAt } = results;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let yPos = margin;

  // Colors
  const primaryColor = [30, 58, 95]; // Navy blue
  const textColor = [51, 51, 51];
  const lightGray = [245, 245, 245];

  // Content translations
  const content = {
    en: {
      title: 'RumahKira',
      subtitle: 'Preliminary Budget Estimation Report',
      projectDetails: 'PROJECT DETAILS',
      houseType: 'House Type',
      builtUpArea: 'Built-Up Area',
      floors: 'Number of Floors',
      finishing: 'Finishing Level',
      budgetRange: 'ESTIMATED BUDGET RANGE',
      minimum: 'Minimum Estimate',
      maximum: 'Maximum Estimate',
      average: 'Average Estimate',
      perSqft: 'Estimated Cost per sq ft',
      breakdown: 'BUDGET ALLOCATION',
      materials: 'ESTIMATED MATERIAL QUANTITIES',
      material: 'Material',
      quantity: 'Estimated Quantity',
      assumptions: 'ASSUMPTIONS',
      basedOn: 'This estimation is based on:',
      excludes: 'EXCLUSIONS:',
      disclaimer: 'DISCLAIMER',
      footer: 'For Planning Purposes Only',
      generated: 'Generated'
    },
    my: {
      title: 'RumahKira',
      subtitle: 'Laporan Anggaran Bajet Awal',
      projectDetails: 'BUTIRAN PROJEK',
      houseType: 'Jenis Rumah',
      builtUpArea: 'Luas Binaan',
      floors: 'Bilangan Tingkat',
      finishing: 'Tahap Kemasan',
      budgetRange: 'JULAT BAJET ANGGARAN',
      minimum: 'Anggaran Minimum',
      maximum: 'Anggaran Maksimum',
      average: 'Anggaran Purata',
      perSqft: 'Anggaran Kos per kaki persegi',
      breakdown: 'PECAHAN BAJET',
      materials: 'ANGGARAN KUANTITI BAHAN',
      material: 'Bahan',
      quantity: 'Anggaran Kuantiti',
      assumptions: 'ANDAIAN',
      basedOn: 'Anggaran ini berdasarkan:',
      excludes: 'PENGECUALIAN:',
      disclaimer: 'PENAFIAN',
      footer: 'Untuk Tujuan Perancangan Sahaja',
      generated: 'Dijana'
    }
  };

  const t = content[language] || content.en;
  const disclaimer = DISCLAIMER[language] || DISCLAIMER.en;

  // Helper function to add a section header
  const addSectionHeader = (text) => {
    doc.setFillColor(...primaryColor);
    doc.rect(margin, yPos, contentWidth, 8, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text(text, margin + 4, yPos + 5.5);
    yPos += 12;
    doc.setTextColor(...textColor);
  };

  // Helper function to add key-value row
  const addKeyValueRow = (key, value, indent = 0) => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(key, margin + indent, yPos);
    doc.setFont('helvetica', 'bold');
    doc.text(value, pageWidth - margin, yPos, { align: 'right' });
    yPos += 6;
  };

  // ===== HEADER =====
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, 35, 'F');

  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text(t.title, pageWidth / 2, 15, { align: 'center' });

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(t.subtitle, pageWidth / 2, 23, { align: 'center' });

  yPos = 45;

  // ===== PROJECT INFO =====
  doc.setTextColor(...textColor);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  const projectName = inputs.projectName || (language === 'my' ? inputs.houseTypeLabelMy : inputs.houseTypeLabel);
  doc.text(`PROJECT: ${projectName}`, margin, yPos);
  yPos += 6;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`${t.generated}: ${formatDate(generatedAt, language)}`, margin, yPos);
  yPos += 12;

  // ===== PROJECT DETAILS =====
  addSectionHeader(t.projectDetails);

  doc.setTextColor(...textColor);
  addKeyValueRow(t.houseType, language === 'my' ? inputs.houseTypeLabelMy : inputs.houseTypeLabel);
  addKeyValueRow(t.builtUpArea, `${formatNumber(inputs.builtUpArea)} sq ft`);
  addKeyValueRow(t.floors, inputs.floors.toString());
  addKeyValueRow(t.finishing, language === 'my' ? inputs.finishingLabelMy : inputs.finishingLabel);

  yPos += 8;

  // ===== BUDGET RANGE =====
  addSectionHeader(t.budgetRange);

  doc.setFillColor(...lightGray);
  doc.rect(margin, yPos - 2, contentWidth, 32, 'F');

  doc.setTextColor(...textColor);
  addKeyValueRow(t.minimum, formatCurrency(costs.minimum), 4);
  addKeyValueRow(t.maximum, formatCurrency(costs.maximum), 4);

  yPos += 2;
  doc.setDrawColor(200, 200, 200);
  doc.line(margin + 4, yPos - 4, pageWidth - margin - 4, yPos - 4);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text(t.average, margin + 4, yPos + 2);
  doc.setFontSize(14);
  doc.setTextColor(...primaryColor);
  doc.text(formatCurrency(costs.average), pageWidth - margin - 4, yPos + 2, { align: 'right' });

  yPos += 10;
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`${t.perSqft}: RM ${costs.perSqftMin} - ${costs.perSqftMax}`, margin + 4, yPos);

  yPos += 14;

  // ===== BUDGET BREAKDOWN =====
  addSectionHeader(t.breakdown);

  doc.setTextColor(...textColor);
  doc.setFontSize(10);

  // Structure
  doc.setFont('helvetica', 'normal');
  doc.text(language === 'my' ? breakdown.structure.labelMy : breakdown.structure.label, margin, yPos);
  doc.setFont('helvetica', 'bold');
  doc.text(`${breakdown.structure.percentage}%`, margin + 80, yPos);
  doc.text(formatCurrency(breakdown.structure.amount), pageWidth - margin, yPos, { align: 'right' });
  yPos += 6;

  // Finishes
  doc.setFont('helvetica', 'normal');
  doc.text(language === 'my' ? breakdown.finishes.labelMy : breakdown.finishes.label, margin, yPos);
  doc.setFont('helvetica', 'bold');
  doc.text(`${breakdown.finishes.percentage}%`, margin + 80, yPos);
  doc.text(formatCurrency(breakdown.finishes.amount), pageWidth - margin, yPos, { align: 'right' });

  yPos += 12;

  // ===== MATERIAL QUANTITIES =====
  addSectionHeader(t.materials);

  const materialData = Object.entries(materials).map(([key, mat]) => [
    language === 'my' ? mat.labelMy : mat.label,
    `± ${formatNumber(mat.quantity)} ${language === 'my' ? mat.unitMy : mat.unit}`
  ]);

  doc.autoTable({
    startY: yPos,
    head: [[t.material, t.quantity]],
    body: materialData,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 9,
      cellPadding: 3
    },
    headStyles: {
      fillColor: [...primaryColor],
      textColor: [255, 255, 255],
      fontStyle: 'bold'
    },
    alternateRowStyles: {
      fillColor: [...lightGray]
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // ===== ASSUMPTIONS =====
  addSectionHeader(t.assumptions);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text(t.basedOn, margin, yPos);
  yPos += 5;

  ASSUMPTIONS.included.forEach((item) => {
    const text = language === 'my' ? item.my : item.en;
    doc.text(`• ${text}`, margin + 4, yPos);
    yPos += 4;
  });

  yPos += 3;
  doc.text(t.excludes, margin, yPos);
  yPos += 5;

  ASSUMPTIONS.excluded.forEach((item) => {
    const text = language === 'my' ? item.my : item.en;
    doc.text(`• ${text}`, margin + 4, yPos);
    yPos += 4;
  });

  yPos += 8;

  // ===== DISCLAIMER =====
  addSectionHeader(t.disclaimer);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);

  disclaimer.content.forEach((item) => {
    const lines = doc.splitTextToSize(`• ${item}`, contentWidth - 8);
    doc.text(lines, margin + 4, yPos);
    yPos += lines.length * 4;
  });

  yPos += 3;
  doc.setFont('helvetica', 'italic');
  const footerLines = doc.splitTextToSize(disclaimer.footer, contentWidth - 8);
  doc.text(footerLines, margin + 4, yPos);

  // ===== FOOTER =====
  const footerY = doc.internal.pageSize.getHeight() - 10;
  doc.setDrawColor(...primaryColor);
  doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`RumahKira • ${t.footer}`, pageWidth / 2, footerY, { align: 'center' });

  // Save the PDF
  const fileName = `RumahKira_${projectName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);

  return fileName;
};
